import {AggregationsChartData} from 'entities/chartData';
import {ChartOutput, DataLabelPosition} from 'entities/chart';
import {
    buildSunburstValueLabelFormatter,
    getSunburstLabelPosition,
} from '@/views/storyboard/utils/chartDataLabels';
import i18n from 'i18n';

export function isHierarchicalAggregationOutput(output?: ChartOutput): boolean {
    return output === ChartOutput.MultiTerms || output === ChartOutput.NestedTerms;
}

export function getHierarchyRootLabel(): string {
    return i18n.t('pivot_row_total.text');
}

export interface TreemapNodeLabelConfig {
    show?: boolean;
    formatter?: (params: {
        name?: string;
        value?: number;
        data?: SunburstTreeNode;
        rect?: { width: number; height: number };
        treePathInfo?: Array<{ name: string }>;
        treeAncestors?: Array<{ name: string }>;
    }) => string;
    height?: number;
    padding?: number | [number, number] | [number, number, number, number];
    align?: 'left' | 'center' | 'right';
    verticalAlign?: 'top' | 'middle' | 'bottom';
}

export interface SunburstLeafNode {
    name: string;
    value: number;
    share: string;
    total: number;
    hierarchyPath?: string;
    itemStyle?: {
        color?: string;
    };
    label?: TreemapNodeLabelConfig;
    upperLabel?: TreemapNodeLabelConfig;
}

export interface SunburstBranchNode {
    name: string;
    value: number;
    children: SunburstTreeNode[];
    itemStyle?: {
        color?: string;
    };
    label?: TreemapNodeLabelConfig;
    upperLabel?: TreemapNodeLabelConfig;
}

export type SunburstTreeNode = SunburstLeafNode | SunburstBranchNode;

interface MutableNode {
    name: string;
    children: Map<string, MutableNode>;
    leaf?: SunburstLeafNode;
}

const EMPTY_KEY_PART = '—';

function splitMultiTermsKey(key: string | number): string[] {
    return String(key).split('|').map((part) => {
        const trimmed = part.trim();
        return trimmed.length > 0 ? trimmed : EMPTY_KEY_PART;
    });
}

export function isAggregationsChartRow(row: unknown): row is AggregationsChartData {
    return (
        typeof row === 'object'
        && row !== null
        && 'docCount' in row
        && typeof (row as AggregationsChartData).docCount === 'number'
        && 'key' in row
        && typeof (row as AggregationsChartData).key === 'string'
    );
}

export function filterAggregationsChartRows(rows: unknown): AggregationsChartData[] {
    if (!Array.isArray(rows)) {
        return [];
    }

    return rows.filter(isAggregationsChartRow);
}

function toLeafNode(item: AggregationsChartData): SunburstLeafNode {
    return {
        name: item.key,
        value: item.docCount,
        share: (item.relativeToAllDocs * 100).toFixed(2),
        total: item.allDocs,
    };
}

export function isHierarchyLeafNode(node: SunburstTreeNode): node is SunburstLeafNode {
    return 'share' in node;
}

export function getHierarchyNodeValue(node: SunburstTreeNode): number {
    if (isHierarchyLeafNode(node)) {
        return node.value;
    }

    return node.children.reduce((sum, child) => sum + getHierarchyNodeValue(child), 0);
}

export function getMultiTermsKeyPartCount(rows: AggregationsChartData[]): number {
    return rows.reduce((max, row) => {
        if (row.key === 'Other') {
            return max;
        }

        return Math.max(max, splitMultiTermsKey(row.key).length);
    }, 0);
}

export function getHierarchyLevelCount(
    nodes: SunburstTreeNode[],
    aggregationFieldCount: number,
    keyPartCount = 0,
): number {
    const treeDepth = getHierarchyTreeDepth(nodes);
    const fieldCount = Math.max(aggregationFieldCount, keyPartCount);

    if (fieldCount >= 2) {
        return Math.max(treeDepth, fieldCount);
    }

    return treeDepth;
}

export function getSunburstLevelCount(
    nodes: SunburstTreeNode[],
    keyPartCount = 0,
): number {
    const treeDepth = getHierarchyTreeDepth(nodes);

    if (keyPartCount >= 2) {
        return Math.max(treeDepth, keyPartCount);
    }

    return treeDepth;
}

/** @deprecated Use getHierarchyLevelCount */
export const getTreemapLevelCount = getHierarchyLevelCount;

export function getHierarchyTreeDepth(nodes: SunburstTreeNode[]): number {
    if (nodes.length === 0) {
        return 0;
    }

    return Math.max(...nodes.map((node) => {
        if (isHierarchyLeafNode(node)) {
            return 1;
        }

        if (node.children.length === 0) {
            return 1;
        }

        return 1 + getHierarchyTreeDepth(node.children);
    }));
}

function ensurePath(root: Map<string, MutableNode>, parts: string[]): MutableNode {
    let currentMap = root;
    let currentNode: MutableNode | undefined;

    for (const part of parts) {
        let node = currentMap.get(part);
        if (!node) {
            node = {name: part, children: new Map()};
            currentMap.set(part, node);
        }
        currentNode = node;
        currentMap = node.children;
    }

    if (!currentNode) {
        throw new Error('Hierarchy path cannot be empty');
    }

    return currentNode;
}

function toSunburstTreeNode(node: MutableNode): SunburstTreeNode {
    if (node.leaf && node.children.size === 0) {
        return node.leaf;
    }

    const children = [...node.children.values()]
        .map(toSunburstTreeNode)
        .sort((a, b) => getHierarchyNodeValue(b) - getHierarchyNodeValue(a));

    if (node.leaf) {
        children.push(node.leaf);
    }

    return {
        name: node.name,
        value: children.reduce((sum, child) => sum + getHierarchyNodeValue(child), 0),
        children,
    };
}

export function aggregationsToSunburstTree(
    rows: AggregationsChartData[],
    isMultiTerms: boolean,
): SunburstTreeNode[] {
    if (!isMultiTerms) {
        return rows
            .map((item) => ({
                ...toLeafNode(item),
                hierarchyPath: item.key,
            }))
            .sort((a, b) => b.value - a.value);
    }

    const roots = new Map<string, MutableNode>();

    for (const item of rows) {
        if (item.key === 'Other') {
            continue;
        }

        const parts = splitMultiTermsKey(item.key);
        if (parts.length < 1) {
            continue;
        }

        const leafName = parts[parts.length - 1];
        const leaf: SunburstLeafNode = {
            ...toLeafNode({...item, key: leafName}),
            hierarchyPath: parts.join('|'),
        };

        if (parts.length === 1) {
            let rootNode = roots.get(leafName);
            if (!rootNode) {
                rootNode = {name: leafName, children: new Map(), leaf};
                roots.set(leafName, rootNode);
            } else {
                rootNode.leaf = leaf;
            }
            continue;
        }

        const parent = ensurePath(roots, parts.slice(0, -1));

        let leafNode = parent.children.get(leafName);
        if (!leafNode) {
            leafNode = {name: leafName, children: new Map(), leaf};
            parent.children.set(leafName, leafNode);
        } else {
            leafNode.leaf = leaf;
        }
    }

    return [...roots.values()]
        .map(toSunburstTreeNode)
        .sort((a, b) => getHierarchyNodeValue(b) - getHierarchyNodeValue(a));
}

function truncateSunburstLabel(name: string, maxLength: number): string {
    if (name.length <= maxLength) {
        return name;
    }

    if (maxLength <= 1) {
        return '…';
    }

    return `${name.slice(0, maxLength - 1)}…`;
}

export function sunburstLabelFormatter(params: { name?: string }): string {
    const name = params.name ?? '';
    if (!name) {
        return '';
    }

    return truncateSunburstLabel(name, 18);
}

const sunburstLabelStyle = {
    color: '#ffffff',
    fontFamily: '"Rubik", sans-serif',
    fontWeight: 500,
    textBorderWidth: 1,
    textBorderColor: 'rgba(0, 0, 0, 0.35)',
    textShadowBlur: 2,
    textShadowColor: 'rgba(0, 0, 0, 0.45)',
    overflow: 'truncate' as const,
    ellipsis: '…',
};

export function buildSunburstLevels(
    dataLevelCount: number,
    radius: [string, string],
    fontSize: number,
    dataLabelPosition: DataLabelPosition = DataLabelPosition.Off,
) {
    if (dataLevelCount <= 0) {
        return [{}];
    }

    const innerRadius = parseFloat(radius[0]);
    const outerRadius = parseFloat(radius[1]);
    const ringWidth = (outerRadius - innerRadius) / dataLevelCount;
    const levels: object[] = [{
        itemStyle: {
            borderWidth: 0,
            borderColor: 'transparent',
        },
        label: {show: false},
    }];

    for (let i = 0; i < dataLevelCount; i++) {
        const isOutermostRing = i === dataLevelCount - 1;
        const ringFontSize = Math.max(9, fontSize - Math.max(0, dataLevelCount - 1 - i));
        const labelMinAngle = isOutermostRing ? 12 : (i === 0 ? 0 : 6);

        levels.push({
            r0: i === 0 ? radius[0] : `${innerRadius + ringWidth * i}%`,
            r: isOutermostRing ? radius[1] : `${innerRadius + ringWidth * (i + 1)}%`,
            itemStyle: {
                borderWidth: 1,
                borderColor: '#ffffff',
            },
            label: {
                ...sunburstLabelStyle,
                rotate: isOutermostRing ? 'radial' : 'tangential',
                show: true,
                minAngle: labelMinAngle,
                fontSize: ringFontSize,
                formatter: buildSunburstValueLabelFormatter(dataLabelPosition, sunburstLabelFormatter),
                ...(getSunburstLabelPosition(dataLabelPosition)
                    ? {position: getSunburstLabelPosition(dataLabelPosition)}
                    : {}),
            },
        });
    }

    return levels;
}
